import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import userApi from '../api/userApi';

interface LanguageContextType {
  language: string;
  setLanguage: (lang: string) => void;
  t: (key: string) => string;
}

const translations: Record<string, Record<string, string>> = {
  id: {
    // Common
    'common.save': 'Simpan',
    'common.cancel': 'Batal',
    'common.delete': 'Hapus',
    'common.edit': 'Edit',
    'common.submit': 'Kirim',
    'common.loading': 'Memuat...',
    'common.error': 'Terjadi kesalahan',
    'common.success': 'Berhasil',

    // Profile
    'profile.title': 'Profil Saya',
    'profile.subtitle': 'Kelola informasi profil dan pengaturan akun Anda',
    'profile.full_name': 'Nama Lengkap',
    'profile.email': 'Email',
    'profile.phone': 'Nomor Telepon',
    'profile.save_changes': 'Simpan Perubahan',
    'profile.saving': 'Menyimpan...',

    // Settings
    'settings.title': 'Pengaturan',
    'settings.subtitle': 'Kelola pengaturan akun dan preferensi Anda',
    'settings.language': 'Bahasa',
    'settings.timezone': 'Zona Waktu',
    'settings.notifications': 'Notifikasi',
    'settings.email_notifications': 'Notifikasi Email',
    'settings.email_notifications_desc': 'Terima notifikasi penting melalui email',
    'settings.push_notifications': 'Push Notifications',
    'settings.push_notifications_desc': 'Terima notifikasi di browser',
    'settings.security_alerts': 'Alert Keamanan',
    'settings.security_alerts_desc': 'Terima notifikasi tentang aktivitas keamanan akun',
    'settings.weekly_digest': 'Ringkasan Mingguan',
    'settings.weekly_digest_desc': 'Terima ringkasan aktivitas mingguan',
    'settings.marketing_emails': 'Email Marketing',
    'settings.marketing_emails_desc': 'Terima email tentang produk dan penawaran baru',
    'settings.preferences': 'Preferensi',
    'settings.select_language': 'Pilih Bahasa',
    'settings.save_settings': 'Simpan Pengaturan',
    'settings.saving': 'Menyimpan...',
  },
  en: {
    // Common
    'common.save': 'Save',
    'common.cancel': 'Cancel',
    'common.delete': 'Delete',
    'common.edit': 'Edit',
    'common.submit': 'Submit',
    'common.loading': 'Loading...',
    'common.error': 'An error occurred',
    'common.success': 'Success',

    // Profile
    'profile.title': 'My Profile',
    'profile.subtitle': 'Manage your profile information and account settings',
    'profile.full_name': 'Full Name',
    'profile.email': 'Email',
    'profile.phone': 'Phone Number',
    'profile.save_changes': 'Save Changes',
    'profile.saving': 'Saving...',

    // Settings
    'settings.title': 'Settings',
    'settings.subtitle': 'Manage your account settings and preferences',
    'settings.language': 'Language',
    'settings.timezone': 'Timezone',
    'settings.notifications': 'Notifications',
    'settings.email_notifications': 'Email Notifications',
    'settings.email_notifications_desc': 'Receive important notifications via email',
    'settings.push_notifications': 'Push Notifications',
    'settings.push_notifications_desc': 'Receive notifications in browser',
    'settings.security_alerts': 'Security Alerts',
    'settings.security_alerts_desc': 'Receive notifications about account security activities',
    'settings.weekly_digest': 'Weekly Digest',
    'settings.weekly_digest_desc': 'Receive weekly activity summary',
    'settings.marketing_emails': 'Marketing Emails',
    'settings.marketing_emails_desc': 'Receive emails about new products and offers',
    'settings.preferences': 'Preferences',
    'settings.select_language': 'Select Language',
    'settings.save_settings': 'Save Settings',
    'settings.saving': 'Saving...',
  },
};

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export function LanguageProvider({ children }: { children: ReactNode }) {
  const [language, setLanguageState] = useState<string>('id');

  useEffect(() => {
    // Load language from localStorage or user settings
    const savedLanguage = localStorage.getItem('language') || 'id';
    setLanguageState(savedLanguage);

    // Try to load from user settings
    loadUserLanguage();
  }, []);

  const loadUserLanguage = async () => {
    try {
      // Try to load language from user settings
      // For now, we'll use localStorage as primary source
      // In the future, can load from user profile settings
    } catch {
      // Silent fail, use default
    }
  };

  const setLanguage = async (lang: string) => {
    setLanguageState(lang);
    localStorage.setItem('language', lang);

    // Save to user settings
    try {
      await userApi.updateSettings({ language: lang } as any);
    } catch (error) {
      console.error('Failed to save language preference:', error);
    }
  };

  const t = (key: string): string => {
    // Try current language first, fallback to Indonesian, then to key itself
    return translations[language]?.[key] || translations['id']?.[key] || key;
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
}
